import * as React from 'react'

import type { Field } from 'factorial-form'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import i18n from 'modules/core/lib/i18n'

import PaymentBill from 'modules/billing/components/PaymentBill'
import PlanModel from 'modules/billing/store/models/Plan'

interface Props {
  plan: PlanModel
  planName: string
  currentSeats: number
  planIntervalReadonly?: boolean
  onTogglePricingPeriod?: (field: Field) => void
  onChangePricingPlan?: (planField: Field) => void
}

interface Prices {
  regular: number
  stroked: string
}

const Bill: React.FC<Props> = ({
  plan,
  planName,
  currentSeats,
  planIntervalReadonly = false,
  onTogglePricingPeriod,
  onChangePricingPlan,
}) => {
  const hasDiscount = React.useMemo(() => {
    return plan.validDiscount
  }, [plan])

  const totalPrices = React.useMemo((): Prices => {
    const price = hasDiscount
      ? plan.discountForNumberOfSeats(currentSeats)
      : plan.priceForNumberOfSeats(currentSeats)

    const stroked = hasDiscount
      ? plan.priceWithCurrency(plan.priceForNumberOfSeats(currentSeats))
      : ''

    return {
      regular: price,
      stroked: stroked,
    }
  }, [plan, currentSeats, hasDiscount])

  const prices = React.useMemo(() => {
    const price = hasDiscount
      ? plan.discountForNumberOfSeats(currentSeats)
      : plan.priceForNumberOfSeats(currentSeats)

    const perMonth = plan.priceWithCurrency(plan.priceForNumberOfSeats(1))

    const stroked = hasDiscount ? i18n.t('billing.bill.per_month', { perMonth }) : null

    return { regular: price, stroked }
  }, [plan, currentSeats, hasDiscount])

  const additionalEmployeePrices = React.useMemo(() => {
    const price = hasDiscount ? plan.discountForNumberOfSeats(1) : plan.priceForNumberOfSeats(1)

    const perMonth = plan.priceWithCurrency(plan.priceForNumberOfSeats(1))

    const stroked = hasDiscount
      ? i18n.t('billing.bill.per_month', {
          perMonth,
        })
      : null

    return {
      regular: price,
      stroked: stroked,
    }
  }, [plan, hasDiscount])

  const planForm = React.useMemo(() => {
    return new Form(
      {
        interval: plan.attributes.interval,
        family: plan.attributes.family,
      },
      {
        interval: 'string',
        family: 'string',
      }
    )
  }, [plan])

  const renderPlanSelector = React.useCallback(() => {
    if (!onChangePricingPlan) return planName

    return (
      <Select
        onChange={onChangePricingPlan}
        field={planForm.get('family')}
        options={[
          {
            label: i18n.t('pricing_plans.plan_names.essentials'),
            value: PlanModel.AVAILABLE_FAMILIES.essentials,
          },
          {
            label: i18n.t('pricing_plans.plan_names.premium'),
            value: PlanModel.AVAILABLE_FAMILIES.premium,
          },
        ]}
      />
    )
  }, [planName, onChangePricingPlan, planForm])

  const renderIntervalSelector = React.useCallback(() => {
    const field = planForm.get('interval')
    const yearlyLabel = i18n.t('pricing_plans.period_switcher.yearly')
    const monthlyLabel = i18n.t('pricing_plans.period_switcher.monthly')

    if (!onTogglePricingPeriod || planIntervalReadonly) {
      return field.value === PlanModel.intervals.year ? yearlyLabel : monthlyLabel
    }

    return (
      <Select
        field={field}
        onChange={onTogglePricingPeriod}
        options={[
          {
            label: yearlyLabel,
            value: 'year',
          },
          {
            label: monthlyLabel,
            value: 'month',
          },
        ]}
      />
    )
  }, [onTogglePricingPeriod, planIntervalReadonly, planForm])

  const additionalEmployeeText = hasDiscount
    ? i18n.t('billing.bill.up_to_employees', {
        numEmployees: 20,
      })
    : ''

  const plusTaxes = i18n.t('billing.bill.plus_taxes')

  const redeemDate = plan.formatedDiscountDurationDate

  const infoDiscount = hasDiscount
    ? i18n.t('billing.bill.discount_until', {
        redeemDate,
      })
    : ''

  return (
    <PaymentBill
      title={i18n.t('billing.payment.plan_title', {
        plan: planName,
      })}
    >
      <PaymentBill.Row>
        <PaymentBill.Text label={i18n.t('billing.bill.plan')} />
        <PaymentBill.Value>{renderPlanSelector()}</PaymentBill.Value>
      </PaymentBill.Row>
      <PaymentBill.Row>
        <PaymentBill.Text label={i18n.t('billing.bill.period')} />
        <PaymentBill.Value>
          <PaymentBill.Value>{renderIntervalSelector()}</PaymentBill.Value>
        </PaymentBill.Value>
      </PaymentBill.Row>
      <PaymentBill.Row>
        <PaymentBill.Text
          label={i18n.t('billing.bill.additional_employee_price')}
          smallLabel={additionalEmployeeText}
        />
        <PaymentBill.Value
          amount={i18n.t('billing.bill.per_month', {
            perMonth: plan.priceWithCurrency(additionalEmployeePrices.regular),
          })}
          strokedAmount={additionalEmployeePrices.stroked}
        />
      </PaymentBill.Row>
      <PaymentBill.Row>
        <PaymentBill.Text label={i18n.t('billing.bill.employees_created')} />
        <PaymentBill.Value amount={currentSeats.toString()} />
      </PaymentBill.Row>
      <PaymentBill.Row>
        <PaymentBill.Text label={i18n.t('billing.bill.total')} smallLabel={infoDiscount} />
        <PaymentBill.Value
          amount={`${plan.priceWithCurrency(totalPrices.regular)} ${plusTaxes}`}
          strokedAmount={totalPrices.stroked}
        />
      </PaymentBill.Row>
    </PaymentBill>
  )
}

export default observer(Bill)
